import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { migrateLegacyLocalStorageSession, syncSessionToServer } from "@/lib/auth-cookie-sync";
import type { User, AuthChangeEvent, Session } from "@supabase/supabase-js";

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  onAuthStateChange?: (event: AuthChangeEvent) => void;
}

export function AuthProvider({ children, onAuthStateChange }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.auth.getUser();
    setUser(error ? null : data.user);
  }, []);

  useEffect(() => {
    let mounted = true;

    // Move any pre-existing localStorage session into the cookie session, then
    // ask the server to (re-)issue it as a first-party Set-Cookie.
    migrateLegacyLocalStorageSession()
      .then(() => refresh())
      .then(async () => {
        if (mounted) setIsLoading(false);
        const { data } = await supabase.auth.getSession();
        if (data.session) void syncSessionToServer(data.session);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session: Session | null) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        void syncSessionToServer(session);
      } else if (event === "SIGNED_OUT") {
        void syncSessionToServer(null);
      }
      refresh();
      onAuthStateChange?.(event);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [refresh, onAuthStateChange]);

  return <AuthContext.Provider value={{ user, isLoading }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
